import { useQueryClient } from '@tanstack/react-query';
import {
  TRAVEL_BOOKING_ADMIN_STATUS_LABELS,
  TRAVEL_BOOKING_DROP_OFF_STAGE_LABELS,
  type TravelBookingAdminBucket,
  type TravelBookingAdminListItem,
  type TravelBookingAdminListQuery,
  type TravelBookingAdminProduct,
  type TravelBookingAdminStatus,
} from '@utazzo/shared';
import { UzButton, UzTooltip } from '@utazzo/ui';
import { Ban, BedDouble, CheckCircle2, Mail, Plane, Undo2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { agoLabel, dateLabel, dateTimeLabel, money } from '../booking-format';
import { listTravelBookings } from '../travel-bookings.api';
import { QK, useDeleteDropOffs, useTravelBookingsList } from '../use-travel-bookings';

import { BookingStatusBadge } from './BookingStatusBadge';
import { BookNowDialog } from './BookNowDialog';
import { CancelBookingDialog } from './CancelBookingDialog';
import { RefundDialog } from './RefundDialog';
import { ResendConfirmationDialog } from './ResendConfirmationDialog';

import {
  type DataGridColumn,
  type DataGridFilter,
  type DataGridState,
} from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { type BaseListQuery } from '@/components/list-screen/list-screen-utils';

/**
 * Which Ticketing page this list is on. The variant decides the server
 * bucket, the filters, the columns and whether rows carry buttons.
 *
 *  - `action`  - everything an admin has to decide or has decided: on hold
 *                (default view), confirmed by admin, cancelled, refunded.
 *  - `booked`  - bookings really made with the supplier, read-only.
 *  - `dropoff` - checkouts abandoned after contact details, deletable.
 */
export type BookingsListVariant = 'action' | 'booked' | 'dropoff';

/** Server search fires only from this many characters. */
const MIN_SEARCH_LENGTH = 3;

/** The "everything" choice on the Action needed view filter. */
const VIEW_ALL = 'ALL';

const ACTION_VIEW_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'On hold - needs a decision' },
  { value: VIEW_ALL, label: 'Everything handled by admins' },
  { value: 'CONFIRMED', label: 'Confirmed by admin' },
  { value: 'CANCELLATION_REQUESTED', label: 'Cancellation requested' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'BOOK_FAILED', label: 'Failed / refunded from hold' },
];

const BOOKED_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = (
  ['CONFIRMED', 'CANCELLATION_REQUESTED', 'CANCELLED', 'REFUNDED', 'BOOK_FAILED'] as const
).map((s) => ({
  value: s,
  label: s === 'BOOK_FAILED' ? 'Partly issued' : TRAVEL_BOOKING_ADMIN_STATUS_LABELS[s],
}));

const BUCKET_FOR: Record<BookingsListVariant, TravelBookingAdminBucket> = {
  action: 'handled',
  booked: 'booked',
  dropoff: 'dropoff',
};

/** Which dialog a row asked for. One at a time, list-wide. */
type RowDialogRequest = {
  kind: 'book' | 'refund' | 'cancel' | 'resend';
  row: TravelBookingAdminListItem;
};

type RowHandlers = {
  onBook: (row: TravelBookingAdminListItem) => void;
  onRefund: (row: TravelBookingAdminListItem) => void;
  onCancel: (row: TravelBookingAdminListItem) => void;
  onResend: (row: TravelBookingAdminListItem) => void;
};

/** Money still with us on this booking. */
const refundable = (row: TravelBookingAdminListItem): boolean =>
  BigInt(row.sellPaise) - BigInt(row.refundedPaise) > 0n && row.paidAt !== null;

/** Why a row's buttons are greyed out - shown as a tooltip. */
function inactiveReason(status: TravelBookingAdminStatus, row: TravelBookingAdminListItem): string {
  switch (status) {
    case 'REFUNDED':
    case 'BOOK_FAILED':
    case 'CANCELLED':
      return refundable(row) ? '' : 'Fully refunded - nothing left to do';
    case 'CANCELLATION_REQUESTED':
      return 'Waiting for the supplier to settle the cancellation';
    case 'EXPIRED':
      return 'Never paid - nothing to book or refund';
    case 'PAID':
    case 'BOOKING':
      return 'Still being processed automatically';
    default:
      return 'No action on this status';
  }
}

/**
 * The decisions on a booking, inline on every row of the Action needed page.
 *
 * Two slots, so the column never changes width: on hold it is Refund and
 * Book now; confirmed it is Cancel and Resend; a cancelled or failed booking
 * that still holds guest money gets Refund alone. Anything else draws the
 * pair disabled with the reason on hover, because an empty column reads as
 * a gap rather than a column. Clicks stop here so the row's own navigation
 * does not fire underneath.
 */
function RowActions({
  row,
  handlers,
  block = false,
}: {
  row: TravelBookingAdminListItem;
  handlers: RowHandlers;
  /** Full-width stacked buttons (phone card) instead of an inline pair. */
  block?: boolean;
}): React.JSX.Element {
  const stop = (e: React.SyntheticEvent): void => e.stopPropagation();
  const wrap = (children: React.ReactNode, single = false): React.JSX.Element => (
    <div
      className={
        block
          ? single
            ? 'grid grid-cols-1'
            : 'grid grid-cols-2 gap-2'
          : 'flex items-center justify-end gap-1.5'
      }
      onClick={stop}
      onKeyDown={stop}
      role="presentation"
    >
      {children}
    </div>
  );
  const btn = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    opts: { primary?: boolean; disabled?: boolean } = {},
  ): React.JSX.Element => (
    <UzButton
      size="sm"
      variant={opts.primary ? 'default' : 'outline'}
      fullWidth={block}
      disabled={opts.disabled}
      className={opts.disabled ? 'pointer-events-none' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      leftIcon={icon}
    >
      {label}
    </UzButton>
  );

  if (row.status === 'ON_HOLD') {
    return wrap(
      <>
        {btn('Refund', <Undo2 className="h-3.5 w-3.5" />, () => handlers.onRefund(row))}
        {btn('Book now', <CheckCircle2 className="h-3.5 w-3.5" />, () => handlers.onBook(row), {
          primary: true,
        })}
      </>,
    );
  }
  if (row.status === 'CONFIRMED') {
    return wrap(
      <>
        {btn('Cancel', <Ban className="h-3.5 w-3.5" />, () => handlers.onCancel(row))}
        {btn('Resend', <Mail className="h-3.5 w-3.5" />, () => handlers.onResend(row))}
      </>,
    );
  }
  if (
    (row.status === 'CANCELLED' || row.status === 'BOOK_FAILED' || row.status === 'REFUNDED') &&
    refundable(row)
  ) {
    return wrap(
      btn('Refund', <Undo2 className="h-3.5 w-3.5" />, () => handlers.onRefund(row), {
        primary: true,
      }),
      true,
    );
  }

  // A disabled <button> receives no pointer events, so a tooltip attached to
  // it never opens. The button is wrapped in a focusable span that carries
  // the tooltip instead, and the button itself lets the hover pass through.
  const reason = inactiveReason(row.status, row);
  const withReason = (button: React.ReactElement): React.ReactElement => (
    <UzTooltip label={reason}>
      <span tabIndex={0} className={block ? 'inline-flex w-full' : 'inline-flex'}>
        {button}
      </span>
    </UzTooltip>
  );
  return wrap(
    <>
      {withReason(
        btn('Refund', <Undo2 className="h-3.5 w-3.5" />, () => undefined, { disabled: true }),
      )}
      {withReason(
        btn('Book now', <CheckCircle2 className="h-3.5 w-3.5" />, () => undefined, {
          primary: true,
          disabled: true,
        }),
      )}
    </>,
  );
}

/** The product icon that leads every reference. */
function ProductIcon({ product }: { product: TravelBookingAdminProduct }): React.JSX.Element {
  return product === 'FLIGHT' ? (
    <Plane className="h-3.5 w-3.5 shrink-0 text-sky-600" aria-hidden />
  ) : (
    <BedDouble className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
  );
}

/** "Yatharth Rathi" with "+2" when others travel too; a dash when nobody was saved. */
function TravellerCell({ row }: { row: TravelBookingAdminListItem }): React.JSX.Element {
  const extra = row.travellerCount - 1;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm wrap-break-word text-zinc-900" title={row.leadName || undefined}>
        {row.leadName || '-'}
        {extra > 0 && <span className="ml-1 text-xs text-zinc-500">+{extra}</span>}
      </span>
      <span className="text-xs text-zinc-500">
        {row.travellerCount} traveller{row.travellerCount === 1 ? '' : 's'}
      </span>
    </div>
  );
}

function StatusCell({ row }: { row: TravelBookingAdminListItem }): React.JSX.Element {
  return (
    <div className="flex flex-col items-start gap-1">
      <BookingStatusBadge status={row.status} />
      {row.status === 'ON_HOLD' && row.holdSince && (
        <span className="text-[11px] text-amber-700">waiting {agoLabel(row.holdSince)}</span>
      )}
      {row.isPartlyIssued && <span className="text-[11px] text-red-700">partly issued</span>}
    </div>
  );
}

/**
 * TBO's margin on one booking, in the supplier's own currency.
 *
 * Three states, and they are genuinely three:
 *
 * - **Unknown** - an em-dash and a tooltip. The supplier response carried no
 *   price block, or the booking predates the capture. It is NOT a zero
 *   variance, and an operator totalling this column needs to see which rows
 *   have nothing in them.
 * - **Zero** - "₹0". TBO settled at exactly what it quoted, which is a real
 *   finding and reads quite differently from the dash beside it.
 * - **Non-zero** - the amount, signed. Positive is coloured because it is the
 *   direction that costs us: the supplier took more than the net we priced the
 *   booking against, straight out of the margin.
 *
 * The settled net goes in the tooltip rather than a column of its own - it is
 * what an operator asks for only once the variance has caught their eye, and
 * the grid has no room for two supplier-currency figures side by side.
 */
/**
 * Our real profit on the booking: markup + the supplier's agency discount + any
 * add-on margin, LESS the payment-gateway fee, GST excluded. Red when it goes
 * negative (a thin margin the gateway fee outweighs).
 */
function NetProfitCell({ row }: { row: TravelBookingAdminListItem }): React.JSX.Element {
  if (row.netProfitPaise === null) {
    return <span className="text-sm text-zinc-400">-</span>;
  }
  const negative = BigInt(row.netProfitPaise) < 0n;
  return (
    <UzTooltip label="Markup + supplier discount + add-on margin, less the gateway fee. GST excluded.">
      <span
        className={`text-sm font-medium tabular-nums ${negative ? 'text-red-700' : 'text-emerald-700'}`}
      >
        {money(row.netProfitPaise, row.currencyCode)}
      </span>
    </UzTooltip>
  );
}

/**
 * One booking as a phone card: reference and status on the first line, the
 * trip, then who and how much, then the dates - and the row's buttons as
 * full-width controls at the bottom where the page has any.
 */
function BookingMobileCard({
  row,
  variant,
  handlers,
}: {
  row: TravelBookingAdminListItem;
  variant: BookingsListVariant;
  handlers: RowHandlers;
}): React.JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-1.5 font-medium text-zinc-900">
            <ProductIcon product={row.product} />
            <span className="truncate tabular-nums">{row.reference}</span>
          </span>
          <span className="truncate text-xs text-zinc-500">
            {row.supplierRef ? `Ref ${row.supplierRef}` : row.title}
          </span>
        </div>
        <div className="shrink-0">
          {variant === 'dropoff' && row.dropOffStage ? (
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-700">
              {TRAVEL_BOOKING_DROP_OFF_STAGE_LABELS[row.dropOffStage]}
            </span>
          ) : (
            <StatusCell row={row} />
          )}
        </div>
      </div>

      <div className="min-w-0">
        <div className="text-sm wrap-break-word text-zinc-900">{row.title}</div>
        <div className="text-xs wrap-break-word text-zinc-500">{row.subtitle}</div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm text-zinc-900">
            {row.leadName || '-'}
            {row.travellerCount > 1 && (
              <span className="ml-1 text-xs text-zinc-500">+{row.travellerCount - 1}</span>
            )}
          </span>
          <span className="truncate text-xs text-zinc-500">
            {row.contactPhone || row.contactEmail}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5 tabular-nums">
          <span className="text-sm font-semibold text-zinc-900">
            {money(row.sellPaise, row.currencyCode)}
          </span>
          {variant === 'booked' && (
            <span className="text-xs text-zinc-500">
              markup {money(row.markupPaise, row.currencyCode)}
            </span>
          )}
          {BigInt(row.refundedPaise) > 0n && (
            <span className="text-xs text-zinc-500">
              refunded {money(row.refundedPaise, row.currencyCode)}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
        <span>
          <span className="text-zinc-400">Travel</span>{' '}
          <span className="text-zinc-700">
            {dateLabel(row.travelStartDate)}
            {row.travelEndDate && row.travelEndDate !== row.travelStartDate
              ? ` to ${dateLabel(row.travelEndDate)}`
              : ''}
          </span>
        </span>
        <span>
          <span className="text-zinc-400">{variant === 'dropoff' ? 'Last seen' : 'Booked'}</span>{' '}
          <span className="text-zinc-700">
            {dateLabel(variant === 'dropoff' ? row.updatedAt : (row.confirmedAt ?? row.createdAt))}
          </span>
        </span>
      </div>

      {variant === 'action' && <RowActions row={row} handlers={handlers} block />}
    </div>
  );
}

/**
 * The list behind every Ticketing page.
 *
 * Newest first everywhere, with the column headers as the sort control. The
 * `from` path rides along to the detail page so its back link returns to
 * the page the row was opened from.
 */
export function BookingsList({
  gridId,
  variant,
  product,
}: {
  gridId: string;
  variant: BookingsListVariant;
  product?: TravelBookingAdminProduct;
}): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const deleteDropOffs = useDeleteDropOffs();

  const [dialog, setDialog] = useState<RowDialogRequest | null>(null);
  const closeDialog = (): void => setDialog(null);
  const afterAction = (): void => {
    // Every list and the header counts: a booking that just left the hold
    // queue must disappear from it everywhere at once.
    void qc.invalidateQueries({ queryKey: QK.all });
  };
  const handlers: RowHandlers = {
    onBook: (row) => setDialog({ kind: 'book', row }),
    onRefund: (row) => setDialog({ kind: 'refund', row }),
    onCancel: (row) => setDialog({ kind: 'cancel', row }),
    onResend: (row) => setDialog({ kind: 'resend', row }),
  };

  const filters = useMemo<ReadonlyArray<DataGridFilter>>(() => {
    switch (variant) {
      case 'action':
        return [{ id: 'view', label: 'View', options: ACTION_VIEW_OPTIONS, widthClass: 'w-64' }];
      case 'booked':
        return [
          {
            id: 'status',
            label: 'Status',
            options: [{ value: '', label: 'Any status' }, ...BOOKED_STATUS_OPTIONS],
            widthClass: 'w-56',
            multi: true,
          },
        ];
      case 'dropoff':
      default:
        return [];
    }
  }, [variant]);

  const columns: ListScreenColumns<TravelBookingAdminListItem> = () => {
    const booking: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'reference',
      header: 'Booking',
      sortable: true,
      // Wide enough for a full `UTZ-FL-2026-000013` reference plus the product
      // icon: the reference is what an operator searches on and pastes into a
      // supplier portal, so it must never be the part that gets an ellipsis.
      width: '230px',
      cell: (row) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-1.5 font-medium text-zinc-900">
            <ProductIcon product={row.product} />
            <span className="truncate tabular-nums">{row.reference}</span>
          </span>
          <span
            className="line-clamp-2 text-xs wrap-break-word text-zinc-500"
            title={`${row.title} · ${row.subtitle}`}
          >
            {row.title}
          </span>
          {row.supplierRef && (
            <span className="truncate text-[11px] text-zinc-400">Ref {row.supplierRef}</span>
          )}
        </div>
      ),
    };
    const traveller: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'traveller',
      header: 'Traveller',
      // Every column carries a width: in the fixed table layout a width-less
      // column only gets what the others leave over, which on a laptop screen
      // was a few pixels and ellipsised the name and contact to nothing. With
      // all widths set, a narrow screen scrolls the table sideways instead.
      width: '160px',
      cell: (row) => <TravellerCell row={row} />,
    };
    const contact: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'contact',
      header: 'Contact',
      width: '190px',
      cell: (row) => (
        <div className="flex min-w-0 flex-col gap-0.5 text-xs">
          <span className="wrap-break-word text-zinc-900">{row.contactPhone || '-'}</span>
          <span className="wrap-break-word text-zinc-500" title={row.contactEmail || undefined}>
            {row.contactEmail || '-'}
          </span>
        </div>
      ),
    };
    const travel: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'travelStartDate',
      header: 'Travel',
      sortable: true,
      width: '120px',
      cell: (row) => (
        <div className="flex flex-col gap-0.5 text-sm">
          <span className="text-zinc-900">{dateLabel(row.travelStartDate)}</span>
          {row.travelEndDate && row.travelEndDate !== row.travelStartDate && (
            <span className="text-xs text-zinc-500">to {dateLabel(row.travelEndDate)}</span>
          )}
        </div>
      ),
    };
    const paid: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'sellPaise',
      header: variant === 'dropoff' ? 'Would have paid' : 'Paid',
      sortable: true,
      width: variant === 'dropoff' ? '140px' : '120px',
      align: 'right',
      cell: (row) => (
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-sm font-medium text-zinc-900">
            {money(row.sellPaise, row.currencyCode)}
          </span>
          {BigInt(row.refundedPaise) > 0n && (
            <span className="text-xs text-zinc-500">
              refunded {money(row.refundedPaise, row.currencyCode)}
            </span>
          )}
        </div>
      ),
    };
    const markup: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'markupPaise',
      header: 'Markup',
      sortable: true,
      width: '105px',
      align: 'right',
      cell: (row) => (
        <span className="text-sm text-zinc-900">{money(row.markupPaise, row.currencyCode)}</span>
      ),
    };
    const netProfit: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'netProfitPaise',
      header: 'Net profit',
      width: '115px',
      align: 'right',
      cell: (row) => <NetProfitCell row={row} />,
    };
    const status: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'status',
      header: 'Status',
      sortable: true,
      width: '150px',
      cell: (row) => <StatusCell row={row} />,
    };
    const booked: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'createdAt',
      header: 'Booked',
      sortable: true,
      width: '120px',
      // The date the booking was actually made with the airline (confirmed),
      // falling back to when the checkout started for rows not yet confirmed.
      cell: (row) => (
        <span
          className="text-sm text-zinc-600"
          title={dateTimeLabel(row.confirmedAt ?? row.createdAt)}
        >
          {dateLabel(row.confirmedAt ?? row.createdAt)}
        </span>
      ),
    };
    const reached: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'stage',
      header: 'Reached',
      width: '150px',
      cell: (row) => (
        <span className="text-sm text-zinc-900">
          {row.dropOffStage ? TRAVEL_BOOKING_DROP_OFF_STAGE_LABELS[row.dropOffStage] : '-'}
        </span>
      ),
    };
    const lastSeen: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'updatedAt',
      header: 'Last seen',
      width: '170px',
      cell: (row) => (
        <span className="text-sm text-zinc-600" title={dateTimeLabel(row.updatedAt)}>
          {dateTimeLabel(row.updatedAt)}
        </span>
      ),
    };
    const actions: DataGridColumn<TravelBookingAdminListItem> = {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      alwaysVisible: true,
      width: '210px',
      align: 'right',
      cell: (row) => <RowActions row={row} handlers={handlers} />,
    };

    switch (variant) {
      case 'action':
        return [booking, traveller, contact, travel, paid, status, booked, actions];
      case 'booked':
        return [booking, traveller, contact, travel, paid, markup, netProfit, status, booked];
      case 'dropoff':
      default:
        return [booking, traveller, contact, reached, paid, travel, lastSeen];
    }
  };

  const toQuery = (state: DataGridState, base: BaseListQuery): TravelBookingAdminListQuery => {
    const { search, sortBy, sortDir, page, pageSize } = base;
    let status: TravelBookingAdminStatus[] | undefined;
    if (variant === 'action') {
      const view = state.filters.view ?? '';
      status =
        view === ''
          ? ['ON_HOLD']
          : view === VIEW_ALL
            ? undefined
            : [view as TravelBookingAdminStatus];
    } else if (variant === 'booked') {
      const raw = state.filters.status ?? '';
      const list = raw.split(',').filter((s) => s.length > 0) as TravelBookingAdminStatus[];
      status = list.length > 0 ? list : undefined;
    }
    return {
      page,
      pageSize,
      sortBy: (sortBy || 'createdAt') as TravelBookingAdminListQuery['sortBy'],
      sortDir,
      ...(search ? { q: search } : {}),
      ...(product ? { product } : {}),
      bucket: BUCKET_FOR[variant],
      ...(status ? { status } : {}),
    };
  };

  const copy = {
    action: {
      title: 'Action needed',
      emptyTitle: 'Nothing needs your attention',
      emptyDescription:
        'Held bookings appear here the moment the supplier refuses one for our account. Switch the view to see what admins already handled.',
    },
    booked: {
      title: product === 'HOTEL' ? 'Hotels' : 'Flights',
      emptyTitle: 'No bookings match these filters',
      emptyDescription: 'Only bookings really made with the supplier appear here.',
    },
    dropoff: {
      title: 'Drop-offs',
      emptyTitle: 'No drop-offs',
      emptyDescription:
        'Checkouts the guest left after entering their email and phone, and never paid for, appear here.',
    },
  }[variant];

  return (
    <ListScreen<TravelBookingAdminListItem, TravelBookingAdminListQuery>
      embedded
      gridId={gridId}
      title={copy.title}
      useList={useTravelBookingsList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      defaultPageSize={20}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={toQuery}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      onRowClick={(row) =>
        navigate(`/bookings/${row.id}`, {
          state: { from: `${location.pathname}${location.search}` },
        })
      }
      renderMobileCard={(row) => (
        <BookingMobileCard row={row} variant={variant} handlers={handlers} />
      )}
      bulkActions={
        variant === 'dropoff'
          ? {
              delete: {
                onDelete: async (rows) => {
                  await deleteDropOffs.mutateAsync(rows.map((r) => r.id));
                },
                confirm: (rows) => ({
                  title: `Delete ${rows.length} drop-off${rows.length === 1 ? '' : 's'}?`,
                  description:
                    'Removes these abandoned checkouts and everything saved on them. Anything that was paid for, or that a payment could still land on, is kept and named afterwards.',
                }),
              },
            }
          : undefined
      }
      overlays={
        dialog ? (
          dialog.kind === 'book' ? (
            <BookNowDialog
              open
              onOpenChange={(open) => {
                if (!open) closeDialog();
              }}
              bookingId={dialog.row.id}
              reference={dialog.row.reference}
              currencyCode={dialog.row.currencyCode}
              onDone={afterAction}
            />
          ) : dialog.kind === 'refund' ? (
            <RefundDialog
              open
              onOpenChange={(open) => {
                if (!open) closeDialog();
              }}
              bookingId={dialog.row.id}
              reference={dialog.row.reference}
              onDone={afterAction}
            />
          ) : dialog.kind === 'cancel' ? (
            <CancelBookingDialog
              open
              onOpenChange={(open) => {
                if (!open) closeDialog();
              }}
              bookingId={dialog.row.id}
              reference={dialog.row.reference}
              product={dialog.row.product}
              onDone={afterAction}
            />
          ) : (
            <ResendConfirmationDialog
              open
              onOpenChange={(open) => {
                if (!open) closeDialog();
              }}
              bookingId={dialog.row.id}
              reference={dialog.row.reference}
              product={dialog.row.product}
              contactEmail={dialog.row.contactEmail}
              onDone={closeDialog}
            />
          )
        ) : null
      }
      export={{
        fetchPage: (q) => listTravelBookings(q),
        filename: `ticketing-${variant}${product ? `-${product.toLowerCase()}` : ''}`,
        csv: [
          { header: 'Reference', value: (r) => r.reference },
          { header: 'Product', value: (r) => r.product },
          { header: 'Status', value: (r) => r.status },
          { header: 'Trip', value: (r) => r.title },
          { header: 'Detail', value: (r) => r.subtitle },
          { header: 'Lead traveller', value: (r) => r.leadName },
          { header: 'Travellers', value: (r) => r.travellerCount },
          { header: 'Email', value: (r) => r.contactEmail },
          { header: 'Phone', value: (r) => r.contactPhone },
          { header: 'Travel from', value: (r) => r.travelStartDate ?? '' },
          { header: 'Travel to', value: (r) => r.travelEndDate ?? '' },
          { header: 'Paid (paise)', value: (r) => r.sellPaise },
          { header: 'Markup (paise)', value: (r) => r.markupPaise },
          // Blank, not 0, for a booking we were never told the settled net for -
          // a spreadsheet that reads 0 will be summed as though the supplier
          // charged exactly what it quoted.
          { header: 'TBO markup (supplier minor)', value: (r) => r.tboMarkupMinor ?? '' },
          {
            header: 'TBO settled net (supplier minor)',
            value: (r) => r.actualSupplierNetMinor ?? '',
          },
          { header: 'Supplier currency', value: (r) => r.supplierCurrency },
          { header: 'Refunded (paise)', value: (r) => r.refundedPaise },
          { header: 'Absorbed delta (paise)', value: (r) => r.absorbedDeltaPaise },
          { header: 'Supplier ref', value: (r) => r.supplierRef ?? '' },
          { header: 'Reached', value: (r) => r.dropOffStage ?? '' },
          { header: 'Booked at', value: (r) => r.createdAt },
          { header: 'Last seen', value: (r) => r.updatedAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search reference, PNR, name, email or phone (min 3 characters)',
        emptyTitle: copy.emptyTitle,
        emptyDescription: copy.emptyDescription,
        errorTitle: 'Failed to load bookings',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
}
